// Typed client for /api/investigations. Row types come from the shared
// schema; view-model extras (counts, joins) are declared here.
import axios from "axios";
import {
  CallParticipant,
  ChatCitation,
  GlossaryEntry,
  Investigation,
  InvestigationCall,
  InvestigationChatMessage,
  InvestigationChatThread,
  InvestigationEntity,
  InvestigationEntityMention,
  InvestigationFile,
  InvestigationBriefVersion,
  InvestigationLead,
  InvestigationTable,
  TimelineOverride,
} from "../../db/schema";

export type BriefVersionItem = InvestigationBriefVersion & {
  newFiles: { id: string; name: string; kind: InvestigationFile["kind"]; callId: string | null; callDate: string | null }[];
};

export type InvestigationListItem = Investigation & {
  role: "owner" | "member";
  fileCount: number;
  callCount: number;
  processingCount: number;
  newLeadCount: number;
  lastActivity: string | null;
};

export type InvestigationAccess = {
  staffView: boolean;
  owner?: { name: string | null; email: string } | null;
  agencyName?: string | null;
};

export type InvestigationDetail = Investigation & {
  role: "owner" | "member";
  // A staff account viewing read-only from the staff portal (StaffView.tsx).
  staffView?: boolean;
  counts: { files: number; processing: number; failed: number; newLeads: number; total: number; rollupRunning: boolean; reanalyzing: number; calls: number };
  members: { user_id: string; role: string; name: string | null; email: string }[];
  // Per-tab "new since you last looked": count of newer items and the stamp
  // the highlights are measured against. See updates.tsx.
  updates: Record<InvestigationTabKey, number>;
  seenAt: Record<InvestigationTabKey, string>;
};

export type InvestigationTabKey = "brief" | "leads" | "calls" | "people" | "timeline";
export type EntityListItem = InvestigationEntity & { last_mention_at: string | null };

export type FileListItem = InvestigationFile & {
  activeJob: { stage: string; status: string; error: string | null } | null;
};

export type SpeakerSuggestion = { label: string; name: string; source: "ai" | "detective"; support: number; conflicts: string[] };

export type CallListItem = InvestigationCall & {
  file: Pick<InvestigationFile, "id" | "original_file_name" | "file_size" | "processing_status" | "error_message" | "privileged_at">;
  // Same-number guess for an unnamed outside speaker (not asserted).
  suggested_outside: { name: string; support: number } | null;
  leadCount: number;
  topSeverity: number;
  confirmedCount: number;
  dismissedCount: number;
  totalLeads: number;
};

export const DISMISS_REASONS: { value: string; label: string; hint: string }[] = [
  { value: "not_relevant", label: "Not relevant", hint: "Real, but not about this case" },
  { value: "already_known", label: "Already known", hint: "Nothing new; recorded for discovery" },
  { value: "ai_misread", label: "AI misread it", hint: "The flag was wrong" },
];

// Relevance after triage: dismissed leads pull a call's displayed score
// down, confirmed ones hold it up. Nothing is hidden; this only reorders.
export function adjustedRelevance(c: { relevance_score: number | null; totalLeads?: number; dismissedCount?: number; confirmedCount?: number }): number | null {
  if (c.relevance_score == null) return null;
  const total = c.totalLeads || 0;
  if (!total) return c.relevance_score;
  const dismissedShare = (c.dismissedCount || 0) / total;
  const confirmedShare = (c.confirmedCount || 0) / total;
  const factor = 1 - dismissedShare * 0.5 + confirmedShare * 0.1;
  return Math.max(0, Math.min(100, Math.round(c.relevance_score * factor)));
}

export type TranscriptSegment = {
  id: number;
  speaker: string;
  channel?: string;
  text: string;
  startMs: number;
  endMs: number;
  english?: string;
};

export type NormalizedTranscript = {
  version: 1;
  jobName: string;
  languages: { code: string; durationSeconds: number }[];
  primaryLanguage: string;
  channelMode: "channel_identification" | "diarization";
  speakerCount: number;
  durationMs: number;
  preambleEndMs: number;
  segments: TranscriptSegment[];
};

export type CallDetailData = InvestigationCall & {
  file: InvestigationFile;
  leads: InvestigationLead[];
  mentions: (InvestigationEntityMention & { entity: Pick<InvestigationEntity, "id" | "canonical_name" | "type"> })[];
  transcript: NormalizedTranscript | null;
  audioUrl: string;
  speaker_suggestions: SpeakerSuggestion[];
  same_number_unnamed: number;
};

// What the pipeline writes to investigation_files.metadata per kind. All
// fields optional: analysis may not have finished, and privileged files
// come back with metadata nulled.
export type FileInsightsMetadata = {
  description?: string;
  documentType?: string;
  keyDates?: { date: string; description: string; page: number | null }[];
  pageCount?: number;
  extractedChars?: number;
  chunkCount?: number;
  sheets?: { name: string; rows: number }[];
  rowCount?: number;
  ocrText?: string;
  // Photos: coded language read off photographed messages or notes. Calls
  // keep theirs on the call row; this is the photo equivalent.
  glossary?: GlossaryEntry[];
  analyzedAt?: string;
  analyzedClass?: "official" | "evidence";
  dateSpan?: { start: string; end: string };
  // Word / plain-text documents: "pages" are text sections, not rendered pages.
  textPages?: boolean;
};

export type FileDetailData = InvestigationFile & {
  callId: string | null;
  url: string | null;
  leads: InvestigationLead[];
  mentions: (InvestigationEntityMention & { entity: Pick<InvestigationEntity, "id" | "canonical_name" | "type"> })[];
  tables: InvestigationTable[];
  // Video files only (see videoFileDetail on the server).
  transcript?: NormalizedTranscript | null;
  participants?: CallParticipant[] | null;
  pipelineStage?: string | null;
  video?: VideoDetail | null;
};

// What the video view reads: metadata.video minus the bulky triage record,
// plus the derived pieces the server adds.
export type VideoDetail = {
  status: "pending" | "extracting" | "analyzing" | "completed" | "failed";
  error?: string;
  progress?: { stage: "triage" | "detail" | "summary" | "check"; done: number; total: number; pct: number; updatedAt: string };
  durationSec?: number;
  width?: number;
  height?: number;
  hasAudio?: boolean;
  frameCount?: number;
  proxyKey?: string;
  watchFor?: string;
  analyzeAll?: boolean;
  summary?: string;
  documentType?: string;
  conditions?: { lighting?: string; weather?: string; location_type?: string };
  eventCount?: number;
  analyzedAt?: string;
  minutes: { minute: number; scene: string; people_count?: number; camera_state: string; activity: "none" | "low" | "high" }[];
  segments: { start_ms: number; end_ms: number; reason: string; priority: number }[];
  idle: { start_ms: number; end_ms: number }[];
  frames: number[];
  motion: number[];
  events: {
    id: string;
    t_start_ms: number;
    t_end_ms: number;
    kind: string;
    description: string;
    actors?: { label: string; role: string; appearance?: string }[];
    objects?: string[];
    confidence: number;
    frame_ts: number[];
    text_read?: string;
    not_visible?: boolean;
  }[];
};

export type LeadListItem = InvestigationLead & {
  fileName: string;
  // The source file's official/evidence classification; leads from official
  // case documents render in their own section of the queue.
  source_class: "official" | "evidence" | null;
  inmate: string | null;
  callDate: string | null;
};

export const isReportLead = (l: Pick<LeadListItem, "source_class">) => l.source_class === "official";

export type ChatMessageView = Pick<InvestigationChatMessage, "id" | "role" | "content" | "citations" | "created_at">;

// The span an allowance covers: the calendar month on a monthly plan, or
// the 12 months of the billing year on an annual one.
export type AllowancePeriod = {
  interval: "month" | "year";
  key: string;
  startMonth: string;
  endMonth: string;
  months: string[];
  label: string;
  monthsRemaining: number;
};

// The department's shared allowance for the period: seats × 50 hours (× 12
// on an annual plan, prorated for mid-year adds), plus heavy seats and any
// hour packs. Null for solo seats.
export type PoolUsage = {
  period: AllowancePeriod;
  seatsPurchased: number;
  minutesUsed: number;
  callsUploaded: number;
  allowanceMinutes: number;
  allowanceCalls: number;
  seatMinutes: number;
  extraSeatMinutes: number;
  creditMinutes: number;
  pct: number;
};

// A solo seat measured against its own allowance for its billing period.
export type PersonalUsage = {
  period: AllowancePeriod;
  minutesUsed: number;
  callsUploaded: number;
  allowanceMinutes: number;
  allowanceCalls: number;
  pct: number;
};

export type UsageSummary = {
  period: string;
  // "pool": the officer's agency shares one allowance and `pool` is what
  // counts; "personal": a solo seat measured against its own allowance.
  scope: "pool" | "personal";
  callsUploaded: number;
  audioMinutes: number;
  allowanceCalls: number | null;
  allowanceMinutes: number | null;
  pool: PoolUsage | null;
  personal: PersonalUsage | null;
  flagLevel: "none" | "watch" | "throttled" | "suspended";
  flagReason: string | null;
};

// How close the officer is to the allowance they are measured against.
// Only the percentage and the period leave the server's shape here: the
// product never shows a running hours count to customers.
export function usageStanding(u: UsageSummary): { pct: number; period: AllowancePeriod } | null {
  const a = u.pool ?? u.personal;
  return a ? { pct: a.pct, period: a.period } : null;
}

const base = "/api/investigations";

export const api = {
  list: () => axios.get<InvestigationListItem[]>(base).then((r) => r.data),
  create: (body: { name: string; case_number?: string; description?: string; focus?: string }) =>
    axios.post<InvestigationListItem>(base, body).then((r) => r.data),
  get: (id: string) => axios.get<InvestigationDetail>(`${base}/${id}`).then((r) => r.data),
  access: (id: string) => axios.get<InvestigationAccess>(`${base}/${id}/access`).then((r) => r.data),
  update: (id: string, body: Partial<Pick<Investigation, "name" | "case_number" | "description" | "focus" | "status">>) =>
    axios.patch<Investigation>(`${base}/${id}`, body).then((r) => r.data),
  remove: (id: string) => axios.delete(`${base}/${id}`),
  attest: (id: string) => axios.post<Investigation>(`${base}/${id}/attest-privilege`, { attested: true }).then((r) => r.data),
  usage: () => axios.get<UsageSummary>(`${base}/me/usage`).then((r) => r.data),

  addMember: (id: string, email: string) => axios.post(`${base}/${id}/members`, { email }),
  removeMember: (id: string, userId: string) => axios.delete(`${base}/${id}/members/${userId}`),

  files: (id: string) => axios.get<FileListItem[]>(`${base}/${id}/files`).then((r) => r.data),
  prepareUploads: (id: string, files: { fileName: string; fileType: string; size: number; sha256?: string }[]) =>
    axios
      .post<{ files: { fileId?: string; fileName: string; kind?: string; uploadURL?: string; error?: string; duplicateOf?: string }[] }>(`${base}/${id}/files/prepare`, { files })
      .then((r) => r.data.files),
  confirmUpload: (id: string, fileId: string, body: { priority?: number; durationSeconds?: number }) =>
    axios.post(`${base}/${id}/files/${fileId}/uploaded`, body),
  fileUrl: (id: string, fileId: string) => axios.get<{ url: string }>(`${base}/${id}/files/${fileId}/url`).then((r) => r.data.url),
  file: (id: string, fileId: string) => axios.get<FileDetailData>(`${base}/${id}/files/${fileId}`).then((r) => r.data),
  setFileClass: (id: string, fileId: string, source_class: "official" | "evidence") =>
    axios.patch<InvestigationFile>(`${base}/${id}/files/${fileId}`, { source_class }).then((r) => r.data),
  importCaseReports: () =>
    axios
      .get<{ id: string; caseNumber: string | null; title: string; status: string; updatedAt: string | null }[]>(`${base}/import/case-reports`)
      .then((r) => r.data),
  importFiles: (id: string, items: { source: "case_audio" | "case_report" | "axon"; id: string; title?: string }[]) =>
    axios
      .post<{ results: { source: string; id: string; fileId?: string; fileName?: string; error?: string }[] }>(`${base}/${id}/files/import`, { items })
      .then((r) => r.data.results),
  retryFile: (id: string, fileId: string) => axios.post(`${base}/${id}/files/${fileId}/retry`),
  markPrivileged: (id: string, fileId: string) => axios.post(`${base}/${id}/files/${fileId}/privileged`),
  deleteFile: (id: string, fileId: string, opts?: { rollup?: boolean }) =>
    axios.delete(`${base}/${id}/files/${fileId}`, { params: opts?.rollup ? { rollup: "true" } : undefined }),
  // Video: the detective's watch-for brief and the analyze-everything switch.
  setVideoOptions: (id: string, fileId: string, body: { watchFor?: string; analyzeAll?: boolean }) =>
    axios.patch<InvestigationFile>(`${base}/${id}/files/${fileId}`, body).then((r) => r.data),
  // A still by second (302 to a presigned URL), usable as an <img src>.
  frameUrl: (id: string, fileId: string, t: number) => `${base}/${id}/files/${fileId}/frame?t=${t}`,

  calls: (id: string) => axios.get<CallListItem[]>(`${base}/${id}/calls`).then((r) => r.data),
  call: (id: string, callId: string) => axios.get<CallDetailData>(`${base}/${id}/calls/${callId}`).then((r) => r.data),
  updateCall: (id: string, callId: string, body: Record<string, unknown>) =>
    axios.patch<InvestigationCall>(`${base}/${id}/calls/${callId}`, body).then((r) => r.data),
  setSpeaker: (id: string, callId: string, body: { label: string; name?: string; role?: string; propagate?: boolean; dismiss?: string }) =>
    axios.post<InvestigationCall & { propagated: number }>(`${base}/${id}/calls/${callId}/speaker`, body).then((r) => r.data),

  leads: (id: string) => axios.get<LeadListItem[]>(`${base}/${id}/leads`).then((r) => r.data),
  // No option → confirmed leads only. `all` → every status; `statuses` picks.
  leadSheetUrl: (id: string, opts: { all?: boolean; statuses?: string[]; format?: "pdf" | "docx" } = {}) => {
    const q = new URLSearchParams();
    if (opts.all) q.set("all", "1");
    else if (opts.statuses?.length) q.set("statuses", opts.statuses.join(","));
    if (opts.format === "docx") q.set("format", "docx");
    const qs = q.toString();
    return `${base}/${id}/leads/lead-sheet.pdf${qs ? `?${qs}` : ""}`;
  },
  updateLead: (id: string, leadId: string, body: { status?: string; note?: string; dismiss_reason?: string }) =>
    axios.patch<InvestigationLead>(`${base}/${id}/leads/${leadId}`, body).then((r) => r.data),
  createLead: (id: string, body: Record<string, unknown>) => axios.post<InvestigationLead>(`${base}/${id}/leads`, body).then((r) => r.data),

  entities: (id: string) => axios.get<EntityListItem[]>(`${base}/${id}/entities`).then((r) => r.data),
  markSeen: (id: string, tab: InvestigationTabKey) => axios.post(`${base}/${id}/seen`, { tab }),
  entity: (id: string, entityId: string) =>
    axios.get<InvestigationEntity & { mentions: (InvestigationEntityMention & { fileName: string })[] }>(`${base}/${id}/entities/${entityId}`).then((r) => r.data),
  updateEntity: (id: string, entityId: string, body: Record<string, unknown>) => axios.patch(`${base}/${id}/entities/${entityId}`, body),
  mergeEntity: (id: string, entityId: string, into: string) => axios.post(`${base}/${id}/entities/${entityId}/merge`, { into }),

  tables: (id: string) => axios.get<(InvestigationTable & { fileName: string })[]>(`${base}/${id}/tables`).then((r) => r.data),
  tableRows: (id: string, tableId: string, offset = 0, limit = 100) =>
    axios.get<{ header: string[]; rows: { row_index: number; data: Record<string, any> }[]; total: number }>(`${base}/${id}/tables/${tableId}/rows`, { params: { offset, limit } }).then((r) => r.data),

  exportToCase: (id: string, body: { instructions?: string; reportTemplateId?: string }) =>
    axios.post<{ caseId: string }>(`${base}/${id}/export-to-case`, body).then((r) => r.data),
  regenerateBrief: (id: string) => axios.post(`${base}/${id}/brief/regenerate`),
  // Hide / annotate a timeline item (key from timelineKey.ts). Returns the
  // whole override map so the tab can swap state in one go.
  setTimelineOverride: (id: string, key: string, body: { hidden?: boolean; note?: string }) =>
    axios.patch<{ overrides: Record<string, TimelineOverride> }>(`${base}/${id}/timeline/${encodeURIComponent(key)}`, body).then((r) => r.data),
  briefVersions: (id: string) => axios.get<BriefVersionItem[]>(`${base}/${id}/brief/versions`).then((r) => r.data),
  reanalyze: (id: string) => axios.post(`${base}/${id}/reanalyze`),
  // Discovery package (ZIP). `format` picks PDF or Word for the documents
  // inside; `?format=json` returns the raw record alone.
  exportUrl: (id: string, format: "pdf" | "docx" = "pdf") => `${base}/${id}/export${format === "docx" ? "?format=docx" : ""}`,
  // Omit `sections` (or pass every key) for the whole summary.
  summaryUrl: (id: string, sections?: string[], format: "pdf" | "docx" = "pdf") => {
    const q = new URLSearchParams();
    if (sections?.length) q.set("sections", sections.join(","));
    if (format === "docx") q.set("format", "docx");
    const qs = q.toString();
    return `${base}/${id}/summary.pdf${qs ? `?${qs}` : ""}`;
  },
  // Timeline as a document; options mirror the Timeline tab's filters.
  timelineUrl: (id: string, o: { include?: string[]; minSeverity?: number; includeHidden?: boolean; includeNotes?: boolean; format?: "pdf" | "docx" } = {}) => {
    const q = new URLSearchParams();
    if (o.include?.length) q.set("include", o.include.join(","));
    if (o.minSeverity) q.set("minSeverity", String(o.minSeverity));
    if (o.includeHidden) q.set("hidden", "1");
    if (o.includeNotes === false) q.set("notes", "0");
    if (o.format === "docx") q.set("format", "docx");
    const qs = q.toString();
    return `${base}/${id}/timeline.pdf${qs ? `?${qs}` : ""}`;
  },
  // Extracted text pages for Word / plain-text documents.
  fileText: (id: string, fileId: string) => axios.get<{ pages: { page: number | null; text: string }[] }>(`${base}/${id}/files/${fileId}/text`).then((r) => r.data),
  transcriptUrl: (id: string, callId: string) => `${base}/${id}/calls/${callId}/transcript.txt`,
  transcriptExportUrl: (id: string, callId: string) => `${base}/${id}/calls/${callId}/transcript-export`,
  // Several calls at once: one file for a single call, a ZIP otherwise.
  transcriptsExportUrl: (id: string) => `${base}/${id}/calls/transcript-export`,

  threads: (id: string) => axios.get<InvestigationChatThread[]>(`${base}/${id}/chat/threads`).then((r) => r.data),
  createThread: (id: string, body: { focusCallId?: string } = {}) => axios.post<InvestigationChatThread>(`${base}/${id}/chat/threads`, body).then((r) => r.data),
  messages: (id: string, threadId: string) =>
    axios.get<{ thread: InvestigationChatThread; messages: ChatMessageView[] }>(`${base}/${id}/chat/threads/${threadId}/messages`).then((r) => r.data),
  archiveThread: (id: string, threadId: string) => axios.delete(`${base}/${id}/chat/threads/${threadId}`),
  chatUrl: (id: string, threadId: string) => `${base}/${id}/chat/threads/${threadId}/messages`,
};

export type { ChatCitation };

// Downloads via fetch + object URL rather than a top-level navigation: the
// CRA dev proxy only forwards requests that don't accept text/html, so a
// plain link to /api/... renders the SPA's 404 in development.
export async function downloadFile(url: string, fallbackName: string, body?: unknown) {
  const res = await fetch(url, {
    credentials: "include",
    ...(body !== undefined
      ? { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }
      : {}),
  });
  if (!res.ok) {
    let msg = "Download failed";
    try {
      msg = (await res.json()).error || msg;
    } catch {
      // not JSON
    }
    throw new Error(msg);
  }
  const blob = await res.blob();
  const disposition = res.headers.get("content-disposition") || "";
  const match = disposition.match(/filename="?([^";]+)"?/);
  const name = match ? match[1] : fallbackName;
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 10_000);
}

export const LEAD_CATEGORY_LABELS: Record<string, string> = {
  violence_threat: "Violence / threat",
  witness_tampering: "Witness tampering",
  third_party_call: "Third-party call",
  drug_transaction: "Drug transaction",
  contraband: "Contraband",
  money_movement: "Money movement",
  coded_language: "Coded language",
  admission: "Admission",
  flight_risk: "Flight risk",
  other: "Other",
  // Official case documents only: what the doc changes, never a threat
  // flag recycled from the officer's own writing.
  discrepancy: "Discrepancy",
  report_note: "New information",
};

// Card highlight while a flagged moment is playing: same hue as the severity chip.
export const SEVERITY_ACTIVE_CLASSES: Record<number, string> = {
  5: "border-red-400 dark:border-red-700 bg-red-50 dark:bg-red-950/40 ring-1 ring-red-200 dark:ring-red-900",
  4: "border-orange-400 dark:border-orange-700 bg-orange-50 dark:bg-orange-950/40 ring-1 ring-orange-200 dark:ring-orange-900",
  3: "border-amber-400 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 ring-1 ring-amber-200 dark:ring-amber-900",
  2: "border-blue-400 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/40 ring-1 ring-blue-200 dark:ring-blue-800",
  1: "border-gray-400 dark:border-base-content/30 bg-gray-100 dark:bg-base-200 ring-1 ring-gray-200 dark:ring-base-300",
};

export const SEVERITY_CLASSES: Record<number, string> = {
  5: "bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-400",
  4: "bg-orange-100 text-orange-800 dark:bg-orange-950/40 dark:text-orange-400",
  3: "bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-400",
  2: "bg-blue-50 text-blue-600 dark:bg-blue-900/40 dark:text-blue-300",
  1: "bg-gray-100 text-gray-700 dark:bg-base-200 dark:text-base-content/70",
};

// Who was on a call, names first. Speaker names come from the transcript's
// participants (AI-identified, or set by the detective); the tag/filename
// metadata (inmate id, dialed number) is the fallback and the subline.
export function callParties(call: Pick<InvestigationCall, "participants" | "inmate_name" | "inmate_id" | "to_number">): {
  primary: string;
  secondary: string | null;
  inmate: string;
  others: string[]; // named outside/third parties, or [dialed number] when none are known
  number: string | null;
} {
  const ps = call.participants || [];
  const named = (role: string) => ps.filter((p) => p.role === role && p.name?.trim()).map((p) => p.name!.trim());
  // Vendor tag/filename name is the booking name; the AI's guess is usually
  // just a first name, so it only fills in when metadata had nothing.
  const inmate = call.inmate_name || named("inmate")[0] || (call.inmate_id ? `#${call.inmate_id}` : "Inmate");
  const outside = [...named("outside"), ...named("third_party")];
  const other = outside.length ? outside.join(", ") : call.to_number || "unknown party";
  const primary = `${inmate} → ${other}`;
  // Only show the number as a subline when it isn't already the headline.
  const secondary = call.to_number && outside.length ? call.to_number : null;
  return { primary, secondary, inmate, others: outside.length ? outside : [other], number: call.to_number || null };
}

export function formatMs(ms: number | null | undefined): string {
  const total = Math.floor((ms || 0) / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export function scoreClass(score: number | null | undefined): string {
  if (score == null) return "bg-gray-100 text-gray-600 dark:bg-base-200 dark:text-base-content/60";
  if (score >= 81) return "bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-400";
  if (score >= 51) return "bg-orange-100 text-orange-800 dark:bg-orange-950/40 dark:text-orange-400";
  if (score >= 21) return "bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-400";
  return "bg-gray-100 text-gray-600 dark:bg-base-200 dark:text-base-content/60";
}

export const PIPELINE_LABELS: Record<string, string> = {
  queued: "Queued",
  transcribing: "Transcribing",
  translating: "Translating",
  analyzing: "Analyzing",
  complete: "Analyzed",
  failed: "Failed",
};

// SHA-256 in the browser, streamed so a 500MB WAV doesn't need to be held
// as one ArrayBuffer twice. Falls back to undefined when SubtleCrypto is
// unavailable (http on a non-localhost origin).
export async function sha256File(file: File): Promise<string | undefined> {
  try {
    if (!window.crypto?.subtle) return undefined;
    const buf = await file.arrayBuffer();
    const digest = await window.crypto.subtle.digest("SHA-256", buf);
    return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, "0")).join("");
  } catch {
    return undefined;
  }
}

export function audioDurationSeconds(file: File): Promise<number | undefined> {
  return new Promise((resolve) => {
    try {
      const url = URL.createObjectURL(file);
      const el = document.createElement("audio");
      el.preload = "metadata";
      const done = (v?: number) => {
        URL.revokeObjectURL(url);
        resolve(v);
      };
      el.onloadedmetadata = () => done(Number.isFinite(el.duration) ? el.duration : undefined);
      el.onerror = () => done(undefined);
      el.src = url;
      setTimeout(() => done(undefined), 8000);
    } catch {
      resolve(undefined);
    }
  });
}
